import joi from 'joi';
import {
  CampaignCardOpenType,
  CampaignCardsFeedComponentAttributes,
  CampaignCardsFeedComponentI18nAttributes,
  CardLayouts,
  CardsFeedLayouts,
  ChannelDisplayType,
} from '@loyalty/editor-components';
import {
  FeedId,
  FeedSearchResult,
} from '@qualifio/data-search-api-client/dist/types/feed-search-result';
import { FormGroup } from '@mui/material';
import { Module } from '@loyalty/shared-common/organisation-api-client';
import { SearchElementType, SearchLanguage } from '@qualifio/data-search-api-client';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSelectField } from './ColumnsSelector';
import { DataSearchField } from '../../../../shared/forms/fields/DataSearchField';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { RowsSelectField } from './RowsSelector';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { getCardLayoutTemplateSelectOptions } from '../utils/card-layout-template-options';
import { useAccounts } from '../../../../accounts/AccountsContext';
import { useApi } from '../../../../shared/api-client/ApiClientProvider';
import { useEditorStore } from '../../../store';
import { useProgram } from '../../../../loyalty/ProgramContext';

export const campaignCardsFeedTraitsSchema = joi.object<TraitFormValue>({
  attributes: joi.object<CampaignCardsFeedComponentAttributes>({
    cardLayoutId: joi.string(),
    feedLayoutId: joi.string(),
    openType: joi.string().valid(...Object.values(CampaignCardOpenType)),
    nbColumns: joi.number().optional().default(3),
    nbRows: joi.number().optional().default(1),
    maxCampaigns: joi.number().optional().default(100).max(100),
  }),
  i18nAttributes: joi.object<CampaignCardsFeedComponentI18nAttributes>({
    feedUuid: joi.string().uuid(),
  }),
});

type CampaignCardsFeedTraitsType = TraitFormValue<
  CampaignCardsFeedComponentAttributes,
  CampaignCardsFeedComponentI18nAttributes
>;

export function CampaignCardsFeedTraits() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<CampaignCardsFeedTraitsType>();
  const { currentAccount } = useAccounts();
  const { programId } = useProgram();

  // Use Zustand store
  const locale = useEditorStore(state => state.locale);

  const { dataSearchApi } = useApi();
  const selectedFeedTemplate = watch('attributes.feedLayoutId');
  const previousI18nAttributes = watch('i18nAttributes');

  const campaignFeedOpenOptions = useMemo(
    () => [
      {
        id: ChannelDisplayType.modal,
        name: t('editor.campaign.traits.channelType.options.modal'),
      },
      {
        id: ChannelDisplayType.newTab,
        name: t('editor.campaign.traits.channelType.options.new-tab'),
      },
    ],
    [t],
  );

  const cardLayoutTemplateOptions = useMemo(() => getCardLayoutTemplateSelectOptions(t), [t]);

  return (
    <FormGroup>
      <Trait>
        <DataSearchField<CampaignCardsFeedTraitsType>
          dataSearchApiClient={dataSearchApi}
          name="i18nAttributes"
          module={Module.LOYALTY}
          externalTenantId={currentAccount.id}
          entityId={programId}
          searchCriteria={{ campaignFeed: true }}
          size="sm"
          languages={[locale.toUpperCase() as SearchLanguage]}
          searchResultToValue={result => {
            if (!result) {
              return undefined;
            }
            const feed = (result as FeedSearchResult).data;
            return {
              ...previousI18nAttributes,
              feedUuid: feed.feedUuid,
            };
          }}
          valueToSearchResult={(value): FeedId | undefined => {
            const feedUuid = (value as CampaignCardsFeedComponentI18nAttributes)?.feedUuid;
            return feedUuid
              ? {
                  type: SearchElementType.campaignFeed,
                  uniqueId: feedUuid,
                }
              : undefined;
          }}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.openType"
          label={t('editor.campaign.traits.channelType.label')}
          defaultValue={CampaignCardOpenType.modal}
          options={campaignFeedOpenOptions}
        />
      </Trait>
      <Trait>
        <InputTextField
          name="attributes.maxCampaigns"
          label={t('editor.campaign-cards-feed.traits.max-campaigns')}
          type={'number'}
          defaultValue={'100'}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.feedLayoutId"
          label={t('editor.campaign-cards-feed.traits.feed-template.label')}
          defaultValue={CardsFeedLayouts.GRID}
          options={[
            { name: 'Grid', id: CardsFeedLayouts.GRID },
            { name: 'Carousel', id: CardsFeedLayouts.CAROUSEL },
          ]}
        />
        <Trait>
          <ColumnSelectField
            name="attributes.nbColumns"
            control={control}
            label={t('editor.campaign-cards-feed.traits.nb-columns')}
          />
          {selectedFeedTemplate === CardsFeedLayouts.GRID && (
            <RowsSelectField
              name="attributes.nbRows"
              control={control}
              label={t('editor.campaign-cards-feed.traits.nb-rows')}
            />
          )}
        </Trait>
      </Trait>

      <Trait>
        <SelectField
          control={control}
          name="attributes.cardLayoutId"
          label={t('editor.card-layout.label')}
          defaultValue={CardLayouts.DEFAULT}
          options={cardLayoutTemplateOptions}
        />
      </Trait>
    </FormGroup>
  );
}
